/**
 * Calentamiento del número (`/activa/oficial/calentamiento`): en qué nivel de Meta está el número, qué pide Meta
 * para subir y qué le falta a la marca para mandar más sin quemarlo. Sólo lee: no frena ni cambia nada.
 *
 * - El nivel, la calidad y el perfil son los que el scheduler lee de Twilio cada hora (§11), no una lectura en vivo:
 *   por eso la página dice cuándo se leyó.
 * - La fila de 7 días cuenta personas distintas por día calendario (hora del negocio); Meta mira 24 h móviles. Es
 *   una aproximación y lo dice.
 * - «Que te escriban primero» arma el link de wa.me y su QR con el número de la marca: quien escribe abre la ventana
 *   de 24 h, y lo que se le conteste ahí no cuenta para el límite.
 * - «Plan de subida» dice hasta qué nivel alcanza la base de hoy (con la fatiga de la marca) y cuánto falta en el
 *   primero que no. Las campañas ya mandan primero a los calientes (`ordenarPorPrioridad` con el calor).
 *
 * La ve también una persona de un cliente: es un GET sin costos. Los arreglos que hace el equipo (Twilio,
 * Configuración, cargar la Base) le llegan con su versión (`arregloPara`).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Circle, CircleDashed, Copy, Download, Flame, Loader2, OctagonPause, RefreshCw, TrendingDown, TrendingUp, XCircle } from "lucide-react";
import type { EstadoCalentamiento } from "../types";
import { oficialApi } from "../api";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { arregloPara, avisoFreno, estadoVisiblePaso, etiquetaDia, hastaDondeLlega, mostrarFilaDeDias, subtituloNivel, textoNivel, textoPasoPlan, textoSiguiente, type EstadoVisiblePaso } from "../logic/calentamiento";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { Tarjeta } from "../components/Tarjeta";
import { fechaHora, num } from "../../campana-nueva/utils/format";

const R = "/activa/oficial";
/** El tope del backend (`MAX_TEXTO_ENTRADA`): un QR con mucho texto cuesta escanearlo. */
const MAX_TEXTO = 200;

const CALIDAD: Record<string, { texto: string; cls: string }> = {
  GREEN: { texto: "Calidad alta", cls: "bg-emerald-50 text-emerald-700" },
  YELLOW: { texto: "Calidad media", cls: "bg-amber-50 text-amber-700" },
  RED: { texto: "Calidad baja", cls: "bg-red-50 text-red-700" },
};
const SIN_MEDIR = { texto: "Calidad sin medir", cls: "bg-slate-100 text-slate-600" };

/** Los pasos de «Lo que mira Meta» como la lista «Para empezar a enviar» del Resumen: cada uno con su estado escrito. */
const ESTADO_PASO: Record<EstadoVisiblePaso, { Icono: typeof CheckCircle2; icono: string; texto: string; chip: string; lector: string }> = {
  hecho: { Icono: CheckCircle2, icono: "text-emerald-600", texto: "Hecho", chip: "bg-emerald-50 text-emerald-700", lector: "Hecho" },
  falta: { Icono: Circle, icono: "text-amber-500", texto: "Falta", chip: "bg-amber-50 text-amber-800", lector: "Falta" },
  frena: { Icono: XCircle, icono: "text-red-600", texto: "Falta", chip: "bg-red-50 text-red-700", lector: "Falta: frena la subida" },
  despues: { Icono: CircleDashed, icono: "text-slate-400", texto: "Se mide después", chip: "bg-slate-100 text-slate-600", lector: "Se mide después" },
};

export default function CalentamientoPage() {
  const equipo = useEsEquipoDeenex();
  const [estado, setEstado] = useState<EstadoCalentamiento | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  // Cada pedido lleva su número: si se piden dos QR seguidos y el primero llega último, no pisa al segundo.
  const ultimoPedido = useRef(0);
  const cargar = useCallback(async (texto?: string) => {
    const pedido = ++ultimoPedido.current;
    setCargando(true);
    let lectura: { estado?: EstadoCalentamiento; error: string };
    try {
      const l = leerRespuesta<EstadoCalentamiento>(await oficialApi.calentamiento(texto));
      lectura = !l.ok ? { error: textoError(l) } : !l.data ? { error: "El servidor no devolvió el estado del número." } : { estado: l.data, error: "" };
    } catch (e) {
      lectura = { error: textoError(leerError(e)) };
    }
    if (pedido !== ultimoPedido.current) return;
    if (lectura.estado) setEstado(lectura.estado);
    setError(lectura.error);
    setCargando(false);
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  if (cargando && !estado) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Cargando el calentamiento">
        {[0, 1, 2].map((i) => <div key={i} className="h-36 rounded-2xl bg-slate-100 animate-pulse" />)}
      </div>
    );
  }
  if (!estado) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <p className="text-sm font-semibold text-red-700">No pudimos traer el estado del número.</p>
        <p className="text-sm text-red-700/80 mt-1">{error}</p>
        <button type="button" onClick={() => void cargar()} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-red-800 hover:underline">
          <RefreshCw size={14} /> Reintentar
        </button>
      </div>
    );
  }

  const calidad = CALIDAD[estado.calidad] || SIN_MEDIR;
  const siguiente = textoSiguiente(estado.nivel.siguiente);
  const freno = avisoFreno(estado);
  const hechos = estado.pasos.filter((p) => estadoVisiblePaso(p) === "hecho").length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">Calentamiento del número</h2>
        <p className="text-sm text-slate-500 mt-1">
          Cuántas personas por día deja Meta escribirle al número, qué pide para subir y qué falta para mandar más sin quemarlo.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" role="alert">
          <AlertTriangle size={15} className="flex-shrink-0" /> {error}
        </div>
      )}

      {!estado.hayNumero && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
          <p className="font-semibold">Todavía no hay un número de WhatsApp conectado.</p>
          <p className="mt-1">
            El nivel, la calidad y el uso se miden cuando esté conectado; lo demás de la lista ya vale.{" "}
            {equipo ? (
              <Link to={`${R}/configuracion`} className="font-semibold underline">Conectarlo en Configuración</Link>
            ) : (
              "Lo conecta el equipo de Deenex."
            )}
          </p>
        </div>
      )}

      {freno && (
        <div
          role="status"
          data-testid="aviso-freno"
          className={`flex items-start gap-2 rounded-2xl border p-4 text-sm ${freno.nivel === "pausa" ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}
        >
          {freno.nivel === "pausa" ? <OctagonPause size={16} className="flex-shrink-0 mt-0.5" /> : <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />}
          <p>
            {freno.texto}
            {freno.nivel === "apagado" && equipo && (
              <>
                {" "}
                <Link to={`${R}/configuracion?seccion=envio`} className="font-semibold underline">Prenderlo en Configuración</Link>
              </>
            )}
          </p>
        </div>
      )}

      <Tarjeta
        titulo="Nivel de Meta"
        sub={subtituloNivel(estado)}
        right={<span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${calidad.cls}`}>{calidad.texto}</span>}
      >
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            <p className="text-xs text-slate-500">Hoy</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="nivel-actual">{textoNivel(estado.nivel)}</p>
          </div>
          {siguiente && (
            <div>
              <p className="text-xs text-slate-500">Siguiente nivel</p>
              <p className="text-lg font-semibold text-slate-700">{siguiente === "ilimitado" ? "Sin límite" : `${siguiente} personas por día`}</p>
            </div>
          )}
        </div>
        <p className="mt-4 text-sm text-slate-700">{estado.motivo}</p>

        {mostrarFilaDeDias(estado.nivel) && (
          <div className="mt-5">
            <p className="text-xs text-slate-500 mb-2">
              Personas distintas por día (Meta pide {num(estado.nivel.mitad || 0)} o más los 7 días): {estado.diasQueCumplen} de 7.
            </p>
            <ol className="grid grid-cols-7 gap-1 sm:gap-1.5" aria-label="Uso de los últimos 7 días">
              {estado.dias.map((d) => (
                <li
                  key={d.dia}
                  className={`rounded-lg px-1 py-2 text-center ${d.cumple ? "bg-emerald-50 text-emerald-800" : "bg-slate-50 text-slate-600"}`}
                  aria-label={`${etiquetaDia(d.dia)}: ${num(d.personas)} personas${d.cumple ? ", llega" : ", no llega"}`}
                >
                  <p className="text-[11px] leading-tight">{etiquetaDia(d.dia)}</p>
                  <p className="text-xs sm:text-sm font-semibold mt-0.5 tabular-nums">{num(d.personas)}</p>
                </li>
              ))}
            </ol>
            <p className="text-xs text-slate-400 mt-2">
              Se cuenta por día, en hora de Argentina; Meta mira 24 h corridas. Lo que contestás a quien te escribió no cuenta.
            </p>
          </div>
        )}
      </Tarjeta>

      <Tarjeta
        titulo="Lo que mira Meta"
        sub="De arriba hacia abajo: lo primero frena más."
        right={<span data-testid="pasos-hechos" className="text-sm font-semibold tabular-nums text-slate-700">{hechos} de {estado.pasos.length} hechos</span>}
      >
        <ol className="divide-y divide-slate-100" aria-label={`Lo que mira Meta: ${hechos} de ${estado.pasos.length} hechos`}>
          {estado.pasos.map((p) => {
            const visible = estadoVisiblePaso(p);
            const { Icono, icono, texto, chip, lector } = ESTADO_PASO[visible];
            const arreglo = arregloPara(p, equipo);
            return (
              <li key={p.id} className="flex items-start gap-3 py-3" data-paso={p.id} data-estado={visible}>
                <Icono size={18} className={`${icono} flex-shrink-0 mt-0.5`} aria-label={lector} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <p className={`text-sm font-semibold ${visible === "hecho" ? "text-slate-600" : "text-slate-900"}`}>{p.titulo}</p>
                    <span className={`sm:ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${chip}`} aria-hidden="true">{texto}</span>
                  </div>
                  <p className="text-sm text-slate-600">{p.detalle}</p>
                  {arreglo && <p className="text-sm text-slate-500 mt-1">{arreglo}</p>}
                </div>
              </li>
            );
          })}
        </ol>
      </Tarjeta>

      {estado.plan && estado.plan.length > 0 && <PlanDeSubida estado={estado} />}

      <QueTeEscriban estado={estado} cargando={cargando} equipo={equipo} onPedir={(t) => void cargar(t)} />

      {estado.historial && <HistorialNumero estado={estado} />}

      <Tarjeta titulo="Para no quemar el número">
        <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
          <li>Sólo a quien dijo que sí a recibir mensajes de la marca por WhatsApp (Meta lo exige, y el art. 5 de la Ley 25.326 también).</li>
          <li>Primero a quien compró hace poco o ya te escribió: es quien menos bloquea y reporta.</li>
          <li>Siempre con la baja a mano: una baja por cualquier canal frena todos los envíos de la marca.</li>
          <li>Subir de a un nivel. Cambiar de número no saltea el límite: desde octubre de 2025 es del negocio en Meta, no del número.</li>
          <li>Nada de bases compradas ni de mensajes entre celulares propios para «calentar»: Meta lo detecta y puede dar de baja el número.</li>
        </ul>
      </Tarjeta>
    </div>
  );
}

function PlanDeSubida({ estado }: { estado: EstadoCalentamiento }) {
  const plan = estado.plan || [];
  const llega = hastaDondeLlega(plan);
  const dias = estado.diasCaliente;
  return (
    <Tarjeta titulo="Plan de subida" sub="Lo mínimo que pide Meta, un nivel por vez. Después decide Meta, y sólo con calidad alta.">
      <p className="text-sm text-slate-700" data-testid="hasta-donde">
        {llega
          ? `Con la base de hoy alcanza para llegar a ${llega.hasta === "ilimitado" ? "sin límite" : `${num(llega.hasta)} personas por día`}${llega.dias > 0 ? `, en ${llega.dias} días como mínimo` : ""}.`
          : `Con la base de hoy no alcanza para el próximo nivel: faltan ${num(plan[0]?.faltan || 0)} contactos con consentimiento.`}
      </p>
      <ol className="mt-4 space-y-3" aria-label="Pasos del plan de subida">
        {plan.map((p) => {
          const t = textoPasoPlan(p);
          return (
            <li key={`${p.desde}-${p.hacia}`} className="flex items-start gap-3">
              {p.alcanza
                ? <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" aria-label="Alcanza" />
                : <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" aria-label="No alcanza" />}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">{t.titulo}</p>
                <p className="text-sm text-slate-600">{t.detalle}</p>
                {!p.alcanza && p.faltan > 0 && <p className="text-sm text-amber-700 mt-0.5">Faltan {num(p.faltan)} contactos con consentimiento.</p>}
              </div>
            </li>
          );
        })}
      </ol>
      {typeof dias === "number" && (
        <p className="text-xs text-slate-400 mt-4">
          Las campañas les mandan primero a los calientes: {dias > 0 ? `los que compraron en los últimos ${dias} días o ya te escribieron` : "los que ya te escribieron"}.
          Son los que menos bloquean, y eso cuida la calidad mientras sube el nivel.
        </p>
      )}
    </Tarjeta>
  );
}

function HistorialNumero({ estado }: { estado: EstadoCalentamiento }) {
  const eventos = estado.historial || [];
  return (
    <Tarjeta titulo="Historial del número" sub="Los cambios de nivel y de calidad de los últimos 90 días">
      {!eventos.length ? (
        <p className="text-sm text-slate-500">{estado.hayNumero ? "Todavía no hay cambios guardados del número." : "Aparece cuando el número esté conectado."}</p>
      ) : (
        <ol className="space-y-2" aria-label="Cambios del número">
          {eventos.map((e, i) => {
            const Icono = e.mejora === true ? TrendingUp : e.mejora === false ? TrendingDown : Circle;
            const cls = e.mejora === true ? "text-emerald-600" : e.mejora === false ? "text-red-600" : "text-slate-400";
            return (
              <li key={`${e.at}-${i}`} className="flex items-start gap-3 text-sm">
                <Icono size={16} className={`${cls} flex-shrink-0 mt-0.5`} aria-hidden="true" />
                {/* La fecha no se parte («07:23 / p. m.»): en el celular va arriba del texto, en la compu al lado. */}
                <span className="flex min-w-0 flex-col sm:flex-row sm:gap-3">
                  <span className="text-slate-500 tabular-nums whitespace-nowrap sm:w-40 sm:flex-shrink-0">{fechaHora(e.at)}</span>
                  <span className="text-slate-700">{e.texto}</span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </Tarjeta>
  );
}

/**
 * Si quien escriba con el texto de este QR queda registrado con su «sí» (consentimiento-qr.service del backend). Vale el
 * texto guardado y el sugerido; un texto editado y sin guardar, no. Guardarlo es del equipo (es la config de la marca).
 */
function ConsentimientoDelQr({ estado, equipo, onGuardado }: { estado: EstadoCalentamiento; equipo: boolean; onGuardado: (texto: string) => void }) {
  const { texto, valeComoConsentimiento } = estado.entrada;
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  if (valeComoConsentimiento === undefined) return null;
  if (valeComoConsentimiento) {
    return (
      <p className="flex items-start gap-1.5 text-xs text-emerald-700" data-testid="consentimiento-qr">
        <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />
        Quien escriba con este mensaje queda registrado con su consentimiento, salvo que tenga una baja o haya dicho que no.
      </p>
    );
  }
  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      const l = leerRespuesta(await oficialApi.actualizarConfig({ textoQr: texto }));
      if (!l.ok) setError(textoError(l));
      else onGuardado(texto);
    } catch (e) {
      setError(textoError(leerError(e)));
    }
    setGuardando(false);
  };
  return (
    <div className="space-y-1.5" data-testid="consentimiento-qr">
      <p className="flex items-start gap-1.5 text-xs text-amber-700">
        <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
        Con este mensaje la persona no queda registrada con su consentimiento: vale el texto guardado.
        {!equipo && " Para que cuente, pedile al equipo de Deenex que lo guarde."}
      </p>
      {equipo && (
        <button type="button" disabled={guardando} onClick={() => void guardar()} className="text-xs font-semibold text-violet-700 hover:underline disabled:opacity-50">
          {guardando ? "Guardando…" : "Guardar este texto para el consentimiento"}
        </button>
      )}
      {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
    </div>
  );
}

function QueTeEscriban({ estado, cargando, equipo, onPedir }: { estado: EstadoCalentamiento; cargando: boolean; equipo: boolean; onPedir: (texto: string) => void }) {
  const { link, qr, texto, textoSugerido } = estado.entrada;
  const [borrador, setBorrador] = useState(texto);
  const [copiado, setCopiado] = useState(false);
  // Lo que volvió del backend manda: si recortó o cambió el texto, el campo lo muestra.
  useEffect(() => { setBorrador(texto); }, [texto]);

  const generar = (e: FormEvent) => {
    e.preventDefault();
    onPedir(borrador.trim() || textoSugerido);
  };
  const copiar = async () => {
    if (!link) return;
    try { await navigator.clipboard?.writeText(link); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* sin portapapeles: el link sigue a la vista */ }
  };

  return (
    <Tarjeta titulo="Que te escriban primero" sub="Un QR para el mostrador, el ticket o las redes">
      <p className="text-sm text-slate-700">
        Quien escanea el QR te escribe con el mensaje ya armado. Esa persona abre una conversación de 24 h: lo que le contestes ahí no cuenta
        para el límite de Meta, y su mensaje queda en Conversaciones como constancia de que lo pidió.
      </p>
      {!link ? (
        <p className="mt-4 text-sm text-slate-500">El QR aparece cuando el número esté conectado.</p>
      ) : (
        <div className="mt-4 flex gap-6 flex-wrap items-start">
          {qr && <img src={qr} alt="QR para escribirle al número de WhatsApp" className="h-44 w-44 rounded-lg border border-slate-100" />}
          <div className="min-w-0 flex-1 space-y-3">
            <form onSubmit={generar} className="space-y-2">
              <label htmlFor="texto-entrada" className="block text-xs font-semibold text-slate-600">Mensaje que llega escrito</label>
              <textarea
                id="texto-entrada"
                value={borrador}
                maxLength={MAX_TEXTO}
                rows={2}
                onChange={(e) => setBorrador(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="submit"
                  disabled={cargando || borrador.trim() === texto}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                >
                  {cargando ? <Loader2 size={14} className="animate-spin" /> : <Flame size={14} />} Actualizar el QR
                </button>
                {texto !== textoSugerido && (
                  <button type="button" onClick={() => onPedir(textoSugerido)} className="text-sm font-semibold text-slate-600 hover:underline">
                    Volver al sugerido
                  </button>
                )}
                <span className="text-xs text-slate-400">{borrador.length}/{MAX_TEXTO}</span>
              </div>
            </form>
            <ConsentimientoDelQr estado={estado} equipo={equipo} onGuardado={onPedir} />
            <p className="text-xs text-slate-500 break-all" data-testid="link-entrada">{link}</p>
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" onClick={() => void copiar()} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:underline">
                <Copy size={14} /> {copiado ? "Copiado" : "Copiar link"}
              </button>
              {qr && (
                <a href={qr} download="qr-whatsapp.png" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:underline">
                  <Download size={14} /> Descargar QR
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </Tarjeta>
  );
}
